import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { videoAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  Upload, FileVideo, CheckCircle2, Loader2, AlertCircle, X, Layers, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

const SUPPORTED_TYPES = {
  'video/mp4': ['.mp4'],
  'video/quicktime': ['.mov'],
  'video/x-msvideo': ['.avi'],
  'video/x-matroska': ['.mkv'],
  'video/webm': ['.webm'],
  'video/x-m4v': ['.m4v'],
  'video/x-flv': ['.flv'],
  'video/x-ms-wmv': ['.wmv'],
  'video/mpeg': ['.mpeg', '.mpg'],
  'video/3gpp': ['.3gp'],
  'video/mp2t': ['.ts', '.mts', '.m2ts'],
  'video/dvd': ['.vob'],
  'video/ogg': ['.ogv'],
  'video/x-ms-asf': ['.asf'],
  'application/mxf': ['.mxf'],
  'video/x-f4v': ['.f4v'],
};

const MAX_SIZE = 500 * 1024 * 1024;
const MAX_BATCH = 10;

const formatSize = (bytes) => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function UploadPage() {
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [status, setStatus] = useState('idle');
  const [videoId, setVideoId] = useState(null);
  const [batchResult, setBatchResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const navigate = useNavigate();

  const isBatch = files.length > 1;

  const onDrop = useCallback((accepted, rejected) => {
    if (rejected.length > 0) {
      const err = rejected[0].errors[0];
      if (err.code === 'file-too-large') toast.error('One or more files too large. Max 500MB each.');
      else if (err.code === 'file-invalid-type') toast.error('Unsupported format detected.');
      else toast.error(err.message);
    }
    setFiles(prev => {
      const combined = [...prev, ...accepted];
      if (combined.length > MAX_BATCH) {
        toast.error(`Maximum ${MAX_BATCH} files per upload.`);
        return combined.slice(0, MAX_BATCH);
      }
      return combined;
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: SUPPORTED_TYPES,
    maxSize: MAX_SIZE,
    multiple: true,
    disabled: status === 'uploading' || status === 'processing',
  });

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    setStatus('uploading');
    setUploadProgress(0);
    setErrorMsg('');

    if (files.length === 1) {
      try {
        const res = await videoAPI.upload(files[0], setUploadProgress);
        setVideoId(res.data.id);
        setStatus('processing');
        toast.success('Upload complete! Processing video...');
        pollStatus(res.data.id);
      } catch (err) {
        setStatus('error');
        setErrorMsg(err.response?.data?.detail || 'Upload failed');
        toast.error('Upload failed');
      }
    } else {
      try {
        const res = await videoAPI.uploadBatch(files, setUploadProgress);
        setBatchResult(res.data);
        setStatus('done');
        toast.success(`Batch uploaded! ${res.data.total} videos queued for processing.`);
      } catch (err) {
        setStatus('error');
        setErrorMsg(err.response?.data?.detail || 'Batch upload failed');
        toast.error('Batch upload failed');
      }
    }
  };

  const pollStatus = async (id) => {
    const poll = setInterval(async () => {
      try {
        const res = await videoAPI.get(id);
        const s = res.data.status;
        if (s === 'ready') {
          clearInterval(poll);
          setStatus('done');
          toast.success('Transcript ready!');
        } else if (s === 'failed') {
          clearInterval(poll);
          setStatus('error');
          setErrorMsg(res.data.error || 'Processing failed');
          toast.error('Processing failed');
        }
      } catch {
        // keep polling
      }
    }, 3000);
  };

  const reset = () => {
    setFiles([]);
    setUploadProgress(0);
    setStatus('idle');
    setVideoId(null);
    setBatchResult(null);
    setErrorMsg('');
  };

  const totalSize = files.reduce((sum, f) => sum + f.size, 0);

  return (
    <div className="max-w-3xl mx-auto p-8 space-y-8" data-testid="upload-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Upload Video</h1>
        <p className="text-muted-foreground mt-2">Drop your video clips and we'll extract the audio, generate transcripts, and get you ready to create fire social posts.</p>
      </div>

      {/* Dropzone */}
      {status === 'idle' && (
        <>
          <Card
            className={`border-2 border-dashed transition-all duration-300 cursor-pointer ${
              isDragActive
                ? 'border-primary bg-primary/5'
                : 'border-border/50 hover:border-primary/40 bg-zinc-900/40'
            }`}
          >
            <CardContent className="p-0">
              <div
                {...getRootProps()}
                className="flex flex-col items-center justify-center py-16 px-6 text-center"
                data-testid="upload-dropzone"
              >
                <input {...getInputProps()} data-testid="upload-input" />
                <div className={`w-20 h-20 rounded-2xl flex items-center justify-center mb-6 transition-all duration-300 ${
                  isDragActive ? 'bg-primary/20 scale-110' : 'bg-secondary/60'
                }`}>
                  <Upload className={`w-10 h-10 ${isDragActive ? 'text-primary' : 'text-muted-foreground'}`} strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl font-semibold mb-2">
                  {isDragActive ? 'Drop files here' : 'Drag & drop your videos'}
                </h3>
                <p className="text-sm text-muted-foreground mb-1">or click to browse files</p>
                <p className="text-xs text-muted-foreground/60 mb-3">Single or multiple files, up to {MAX_BATCH} at a time</p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {['.mp4', '.mov', '.avi', '.mkv', '.webm'].map((fmt) => (
                    <span key={fmt} className="text-[10px] uppercase tracking-wider text-muted-foreground/60 bg-secondary/40 px-2 py-1 rounded-md">{fmt}</span>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground/40 mt-3">Max 500MB per file</p>
              </div>
            </CardContent>
          </Card>

          {/* Selected files list */}
          {files.length > 0 && (
            <Card className="bg-zinc-900/60 border-white/5" data-testid="selected-files-list">
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-heading text-sm font-semibold">{files.length} {files.length === 1 ? 'file' : 'files'} selected</h4>
                    {files.length > 1 && (
                      <Badge variant="secondary" className="text-[10px]">{formatSize(totalSize)} total</Badge>
                    )}
                  </div>
                  {files.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => setFiles([])} className="text-muted-foreground hover:text-destructive text-xs" data-testid="clear-all-files">
                      Clear All
                    </Button>
                  )}
                </div>

                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {files.map((file, index) => (
                    <div key={`${file.name}-${index}`} className="flex items-center justify-between p-3 rounded-lg bg-secondary/20 border border-white/5" data-testid={`file-item-${index}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <FileVideo className="w-4 h-4 text-primary" strokeWidth={1.5} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{file.name}</p>
                          <p className="text-[10px] text-muted-foreground">{formatSize(file.size)}</p>
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => removeFile(index)} data-testid={`remove-file-${index}`}>
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>

                <Button
                  onClick={handleUpload}
                  className="w-full rounded-full bg-primary hover:bg-primary/90 shadow-lg shadow-indigo-500/25 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] mt-2"
                  data-testid="start-upload-button"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {isBatch ? `Upload & Process ${files.length} Videos` : 'Upload & Process'}
                </Button>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Uploading State */}
      {status === 'uploading' && (
        <Card className="bg-zinc-900/60 border-white/5" data-testid="upload-progress-card">
          <CardContent className="p-8">
            <div className="flex flex-col items-center text-center space-y-6">
              <Loader2 className="w-12 h-12 animate-spin text-primary" />
              <div>
                <h3 className="font-heading text-xl font-semibold">
                  {isBatch ? `Uploading ${files.length} Videos` : 'Uploading'}
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {isBatch ? formatSize(totalSize) + ' total' : files[0]?.name}
                </p>
              </div>
              <div className="w-full max-w-md space-y-2">
                <Progress value={uploadProgress} className="h-2" />
                <p className="text-sm text-muted-foreground">{uploadProgress}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Processing State (single only) */}
      {status === 'processing' && (
        <Card className="bg-zinc-900/60 border-white/5" data-testid="processing-card">
          <CardContent className="p-8">
            <div className="flex flex-col items-center text-center space-y-6">
              <div className="w-16 h-16 rounded-full border-4 border-primary/20 border-t-primary animate-spin" />
              <div>
                <h3 className="font-heading text-xl font-semibold">Processing Video</h3>
                <p className="text-sm text-muted-foreground mt-1">Extracting audio and generating transcript...</p>
                <p className="text-xs text-muted-foreground/60 mt-2">This may take a minute depending on video length</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Done State */}
      {status === 'done' && (
        <Card className="bg-zinc-900/60 border-emerald-500/20" data-testid="upload-done-card">
          <CardContent className="p-8">
            <div className="flex flex-col items-center text-center space-y-6">
              <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-400" />
              </div>
              <div>
                {videoId ? (
                  <>
                    <h3 className="font-heading text-xl font-semibold">Transcript Ready</h3>
                    <p className="text-sm text-muted-foreground mt-1">Your video has been processed. Review the transcript and generate social posts.</p>
                  </>
                ) : (
                  <>
                    <h3 className="font-heading text-xl font-semibold">Batch Queued</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {batchResult?.total} videos are being processed sequentially. Track progress in the Pipeline view.
                    </p>
                    {batchResult?.upload_errors?.length > 0 && (
                      <div className="mt-3 text-left mx-auto max-w-sm">
                        <p className="text-xs text-destructive font-medium mb-1">{batchResult.upload_errors.length} files skipped:</p>
                        {batchResult.upload_errors.map((err, i) => (
                          <p key={i} className="text-[10px] text-muted-foreground">{err.filename}: {err.error}</p>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="flex gap-3">
                {videoId && (
                  <Button
                    onClick={() => navigate(`/video/${videoId}`)}
                    className="rounded-full bg-primary hover:bg-primary/90 shadow-lg shadow-indigo-500/25"
                    data-testid="review-transcript-button"
                  >
                    Review Transcript
                  </Button>
                )}
                {batchResult && (
                  <Button
                    onClick={() => navigate('/pipeline')}
                    className="rounded-full bg-primary hover:bg-primary/90 shadow-lg shadow-indigo-500/25"
                    data-testid="view-pipeline-button"
                  >
                    <Layers className="w-4 h-4 mr-2" /> View Pipeline
                  </Button>
                )}
                <Button variant="secondary" onClick={reset} className="rounded-full" data-testid="upload-more-button">
                  Upload More
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {status === 'error' && (
        <Card className="bg-zinc-900/60 border-destructive/20" data-testid="upload-error-card">
          <CardContent className="p-8">
            <div className="flex flex-col items-center text-center space-y-6">
              <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-destructive" />
              </div>
              <div>
                <h3 className="font-heading text-xl font-semibold">Something Went Wrong</h3>
                <p className="text-sm text-muted-foreground mt-1">{errorMsg}</p>
              </div>
              <Button onClick={reset} className="rounded-full" variant="secondary" data-testid="try-again-button">
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
